// src/pages/Checkout/CheckoutPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../Context/CartContext";
import { useCurrency } from "../Context/CurrencyContext";
import { createOrder, initiatePaymentAPI, verifyPaymentAPI } from "../pages/api";
import "./CheckoutPage.css";

const CheckoutPage = () => {
  const { cartItems, subtotal, clearCart } = useCart();
  const { formatPrice } = useCurrency(); // Use currency context
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSavedAddresses, setShowSavedAddresses] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    return !!(token && isLoggedIn);
  });
  const [showLoginOption, setShowLoginOption] = useState(false);
  const [currentStep, setCurrentStep] = useState("delivery");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card");
  
  // Payment popup states
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({});

  const userEmail = localStorage.getItem("userEmail") || "";

  const [savedAddresses, setSavedAddresses] = useState(() => {
    if (userEmail) {
      try {
        const addresses = localStorage.getItem(`profileAddresses_${userEmail}`);
        return addresses ? JSON.parse(addresses) : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  const [formData, setFormData] = useState(() => {
    const defaultAddress = savedAddresses.find((addr) => addr.isDefault) || savedAddresses[0];
    if (defaultAddress) {
      const nameParts = defaultAddress.fullName.split(" ");
      return {
        email: userEmail || "",
        country: defaultAddress.country || "India",
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        address: defaultAddress.address1 || "",
        apartment: defaultAddress.address2 || "",
        city: defaultAddress.city || "",
        state: defaultAddress.state || "",
        pinCode: defaultAddress.postalCode || "",
        phone: defaultAddress.phone || "",
        paymentMethod: "card",
      };
    }
    return {
      email: userEmail || "",
      country: "India",
      firstName: "",
      lastName: "",
      address: "",
      apartment: "",
      city: "",
      state: "",
      pinCode: "",
      phone: "",
      paymentMethod: "card",
    };
  });

  // Check for payment return (verification)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get("orderId");
    const transactionId = params.get("transactionId") || params.get("txnid");
    const status = params.get("status") || (params.get("mihpayid") ? "success" : null);
    const paymentMethod = params.get("paymentMethod");

    if (orderId && transactionId && status) {
      console.log("🔁 Payment return detected, verifying...", { orderId, transactionId, status, paymentMethod });

      const verifyReturnedPayment = async () => {
        setLoading(true);
        try {
          const verification = await verifyPaymentAPI({
            orderId,
            transactionId,
            paymentMethod: paymentMethod || selectedPaymentMethod,
          });

          console.log("✅ Verification response:", verification);

          if (verification && verification.success) {
            // Show success popup
            setPaymentStatus('success');
            setPaymentDetails({
              orderId: verification.orderId || orderId,
              transactionId: transactionId,
              amount: subtotal,
              paymentMethod: verification.paymentMethod || selectedPaymentMethod
            });
            setShowPaymentPopup(true);
            
            clearCart();
            localStorage.setItem(
              "lastOrder",
              JSON.stringify({
                orderId: verification.orderId || orderId,
                total: subtotal,
                shippingAddress: formData,
                items: cartItems,
                date: new Date().toISOString(),
                paymentMethod: verification.paymentMethod || selectedPaymentMethod,
                transactionId: transactionId
              })
            );

            setCurrentStep("review");
          } else {
            // Show failure popup
            setPaymentStatus('failure');
            setPaymentDetails({
              orderId: orderId,
              transactionId: transactionId,
              amount: subtotal,
              paymentMethod: paymentMethod || selectedPaymentMethod,
              errorMessage: "Payment verification failed"
            });
            setShowPaymentPopup(true);
            setErrors({ submit: "Payment verification failed. Please try again." });
            setCurrentStep("payment");
          }
        } catch (error) {
          console.error("❌ Verification error:", error);
          
          // Show failure popup
          setPaymentStatus('failure');
          setPaymentDetails({
            orderId: orderId,
            transactionId: transactionId,
            amount: subtotal,
            paymentMethod: paymentMethod || selectedPaymentMethod,
            errorMessage: error.message || "Could not verify payment"
          });
          setShowPaymentPopup(true);
          
          setErrors({ submit: "Could not verify payment. Contact support." });
          setCurrentStep("payment");
        } finally {
          setLoading(false);
        }
      };

      verifyReturnedPayment();
    }
  }, [location.search, clearCart, navigate, formData, cartItems, subtotal, selectedPaymentMethod]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    setIsAuthenticated(!!(token && isLoggedIn));
  }, []);

  useEffect(() => {
    const defaultAddress = savedAddresses.find((addr) => addr.isDefault) || savedAddresses[0];
    if (defaultAddress && !formData.address) {
      const nameParts = defaultAddress.fullName.split(" ");
      setFormData((prev) => ({
        ...prev,
        email: userEmail || "",
        country: defaultAddress.country || "India",
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        address: defaultAddress.address1 || "",
        apartment: defaultAddress.address2 || "",
        city: defaultAddress.city || "",
        state: defaultAddress.state || "",
        pinCode: defaultAddress.postalCode || "",
        phone: defaultAddress.phone || "",
      }));
    }
  }, [savedAddresses, userEmail]);

  // Helper functions
  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.pinCode.trim()) {
      newErrors.pinCode = "PIN code is required";
    } else if (!/^\d{6}$/.test(formData.pinCode)) {
      newErrors.pinCode = "PIN code must be 6 digits";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (cartItems.length === 0) newErrors.cart = "Your cart is empty";

    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
    setFormData((prev) => ({ ...prev, paymentMethod: method }));
  };

  const handleSelectAddress = (address) => {
    const nameParts = address.fullName.split(" ");
    setFormData((prev) => ({
      ...prev,
      email: userEmail || "",
      country: address.country || "India",
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || "",
      address: address.address1 || "",
      apartment: address.address2 || "",
      city: address.city || "",
      state: address.state || "",
      pinCode: address.postalCode || "",
      phone: address.phone || "",
    }));
    setShowSavedAddresses(false);
  };

  const handleContinueToPayment = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const token = localStorage.getItem("token");
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    const isAuth = !!(token && isLoggedIn);
    setIsAuthenticated(isAuth);

    if (!isAuth) {
      setShowLoginOption(true);
      setCurrentStep("payment");
    } else {
      setShowLoginOption(false);
      setCurrentStep("payment");
    }
  };

  const handleBackToDelivery = () => {
    setCurrentStep("delivery");
    setShowLoginOption(false);
  };

  const handleLoginRedirect = () => {
    navigate(`/login?redirect=${encodeURIComponent("/checkout")}`);
  };

  // Handle popup close
  const handleClosePopup = () => {
    setShowPaymentPopup(false);
    
    // If payment was successful, navigate to review step
    if (paymentStatus === 'success') {
      // Clear URL parameters
      navigate('/checkout', { replace: true });
      setCurrentStep("review");
    } else if (paymentStatus === 'failure') {
      // Just close popup, stay on payment page
      navigate('/checkout', { replace: true });
    }
    
    // Reset payment status after popup closes
    setTimeout(() => {
      setPaymentStatus(null);
      setPaymentDetails({});
    }, 300);
  };

  // Order creation
  const createOrderFirst = async () => {
    try {
      setLoading(true);
      const orderData = {
        shippingStreet: formData.address,
        shippingZipCode: formData.pinCode,
        shippingCity: formData.city,
        shippingState: formData.state,
        shippingCountry: formData.country,
        shippingPhone: formData.phone,
        shippingEmail: formData.email,
        shippingFirstName: formData.firstName || "",
        shippingLastName: formData.lastName,
        shippingAddress2: formData.apartment || "",
        items: cartItems.map((item) => ({
          productId: item.id || item.productId,
          productName: item.name || item.title,
          price: item.price || item.priceValue,
          quantity: item.qty || 1,
          image: item.image || item.img || "",
          selectedAttributes: item.selectedAttributes || [
            item.selectedSize || "",
            item.selectedFragrance || "",
          ],
          selectedAttributeDetails: item.selectedAttributeDetails || [],
        })),
        paymentMethod: selectedPaymentMethod,
        paymentStatus: "pending",
        orderStatus: "pending",
        subtotal: subtotal,
        shipping: 0,
        tax: 0,
        total: subtotal,
        notes: "",
        isGuestOrder: !isAuthenticated,
        customerEmail: formData.email,
        customerPhone: formData.phone,
      };

      console.log("📦 Creating order with data:", orderData);
      const response = await createOrder(orderData);
      console.log("✅ Order created, response:", response);

      let orderId;
      if (response) {
        if (response._id) orderId = response._id;
        else if (response.data && response.data._id) orderId = response.data._id;
        else if (response.orderId) orderId = response.orderId;
        else if (response.id) orderId = response.id;
      }
      if (!orderId) throw new Error("Failed to get order ID from response");
      return orderId;
    } catch (error) {
      console.error("❌ Order creation error:", error);
      let errorMessage = "Failed to create order. Please try again.";
      if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setErrors({ submit: errorMessage });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Payment initiation + redirect
  const handlePayNow = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);
    try {
      // 1. Create order
      const orderId = await createOrderFirst();

      // 2. If COD -> success immediately
      if (selectedPaymentMethod === "cod") {
        setPaymentStatus('success');
        setPaymentDetails({
          orderId: orderId,
          amount: subtotal,
          paymentMethod: "cod"
        });
        setShowPaymentPopup(true);
        
        clearCart();
        localStorage.setItem(
          "lastOrder",
          JSON.stringify({
            orderId,
            total: subtotal,
            shippingAddress: formData,
            items: cartItems,
            date: new Date().toISOString(),
            paymentMethod: "cod",
          })
        );
        
        setCurrentStep("review");
        setLoading(false);
        return;
      }

      // 3. For online payments: initiate payment with success/cancel URLs
      const successUrl = `${window.location.origin}/checkout?orderId=${orderId}&paymentMethod=${selectedPaymentMethod}&status=success&transactionId={transactionId}`;
      const cancelUrl = `${window.location.origin}/checkout?orderId=${orderId}&paymentMethod=${selectedPaymentMethod}&status=cancelled`;

      const paymentPayload = {
        orderId,
        amount: subtotal,
        currency: "INR",
        paymentMethod: selectedPaymentMethod,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        successUrl,
        cancelUrl,
      };

      console.log("💳 Initiating payment with payload:", paymentPayload);
      const paymentResponse = await initiatePaymentAPI(paymentPayload);
      console.log("✅ Payment initiation response:", paymentResponse);

      // Handle PayU specific response
      if (paymentResponse?.data?.paymentUrl && paymentResponse?.data?.params) {
        const { paymentUrl, params } = paymentResponse.data;
        console.log("➡️ Submitting PayU form to:", paymentUrl, params);

        // Dynamically create and submit a POST form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentUrl;
        form.style.display = 'none';

        // Add all params as hidden input fields
        Object.keys(params).forEach(key => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = params[key];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        return; // Stop further execution – page will redirect
      }

      // Fallback: try other common response structures
      const redirectUrl = paymentResponse.redirectUrl ||
                         paymentResponse.data?.redirectUrl ||
                         paymentResponse.paymentUrl ||
                         paymentResponse.data?.paymentUrl ||
                         paymentResponse.url;

      if (redirectUrl) {
        console.log("➡️ Redirecting to (GET):", redirectUrl);
        window.location.href = redirectUrl;
      } else {
        throw new Error("No redirect URL or payment parameters received from payment gateway");
      }
    } catch (error) {
      console.error("❌ Payment initiation error:", error);
      
      // Show failure popup for payment initiation errors
      setPaymentStatus('failure');
      setPaymentDetails({
        errorMessage: error.message || "Failed to initiate payment"
      });
      setShowPaymentPopup(true);
      
      setErrors({ submit: error.message || "Failed to initiate payment" });
      setLoading(false);
    }
  };

  // Back / Utility
  const handleBackToCart = () => navigate("/cart");
  const handleContinueShopping = () => navigate("/");
  const handleViewOrderDetails = () => {
    const lastOrder = JSON.parse(localStorage.getItem("lastOrder") || "{}");
    if (lastOrder.orderId) navigate(`/order/${lastOrder.orderId}`);
  };

  // Render
  return (
    <>
      <Header />
      <div className="checkout-wrapper">
        <div className="checkout-left">
          {currentStep === "delivery" && (
            <>
              <h2 className="checkout-title">Delivery</h2>
              {errors.submit && <div className="error-message">{errors.submit}</div>}
              {savedAddresses.length > 0 && (
                <div className="saved-addresses-section">
                  <div className="saved-addresses-header">
                    <button
                      className="toggle-saved-addresses"
                      onClick={() => setShowSavedAddresses(!showSavedAddresses)}
                    >
                      {showSavedAddresses ? "Hide saved addresses" : "Use a saved address"}
                    </button>
                  </div>
                  {showSavedAddresses && (
                    <div className="saved-addresses-list">
                      {savedAddresses.map((address) => (
                        <div
                          key={address.id}
                          className={`saved-address-card ${address.isDefault ? "default" : ""}`}
                        >
                          <div className="address-card-header">
                            {address.isDefault && <span className="default-badge">Default</span>}
                            <h4>{address.fullName}</h4>
                          </div>
                          <p className="address-details">
                            {address.address1}
                            <br />
                            {address.address2 && (
                              <>
                                {address.address2}
                                <br />
                              </>
                            )}
                            {address.city}, {address.state} {address.postalCode}
                          </p>
                          <div className="saved-address-actions">
                            <button
                              className="select-address-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectAddress(address);
                              }}
                            >
                              Use this address
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="checkout-form">
                {/* Country */}
                <div className="field-group">
                  <label>Country/Region</label>
                  <select
                    name="country"
                    className={`checkout-select ${errors.country ? "input-error" : ""}`}
                    value={formData.country}
                    onChange={handleInputChange}
                  >
                    <option value="India">India</option>
                    <option value="United States">United States</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                  </select>
                  {errors.country && <span className="error-text">{errors.country}</span>}
                </div>
                {/* Name fields */}
                <div className="row-2">
                  <input
                    name="firstName"
                    placeholder="First name (optional)"
                    className="checkout-input"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                  <input
                    name="lastName"
                    placeholder="Last name"
                    className={`checkout-input ${errors.lastName ? "input-error" : ""}`}
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                {/* Address */}
                <div className="field-group">
                  <input
                    name="address"
                    placeholder="Address"
                    className={`checkout-input ${errors.address ? "input-error" : ""}`}
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </div>
                <div className="field-group">
                  <input
                    name="apartment"
                    placeholder="Apartment, suite, etc. (optional)"
                    className="checkout-input"
                    value={formData.apartment}
                    onChange={handleInputChange}
                  />
                </div>
                {/* City, State, Pin */}
                <div className="row-3">
                  <div className="field-group">
                    <input
                      name="city"
                      placeholder="City"
                      className={`checkout-input ${errors.city ? "input-error" : ""}`}
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                    {errors.city && <span className="error-text">{errors.city}</span>}
                  </div>
                  <div className="field-group">
                    <input
                      name="state"
                      placeholder="State"
                      className={`checkout-input ${errors.state ? "input-error" : ""}`}
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                    {errors.state && <span className="error-text">{errors.state}</span>}
                  </div>
                  <div className="field-group">
                    <input
                      name="pinCode"
                      placeholder="PIN code"
                      className={`checkout-input ${errors.pinCode ? "input-error" : ""}`}
                      value={formData.pinCode}
                      onChange={handleInputChange}
                    />
                    {errors.pinCode && <span className="error-text">{errors.pinCode}</span>}
                  </div>
                </div>
                {/* Email */}
                <div className="field-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    className={`checkout-input ${errors.email ? "input-error" : ""}`}
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
                {/* Phone */}
                <div className="field-group">
                  <label>Phone number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter your phone number"
                    className={`checkout-input ${errors.phone ? "input-error" : ""}`}
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
                {/* Shipping method placeholder */}
                <div className="shipping-method-section">
                  <h3>Shipping method</h3>
                  <p className="shipping-text">
                    Enter your shipping address to view available shipping methods.
                  </p>
                </div>
                {errors.cart && <div className="error-message">{errors.cart}</div>}
                <div className="checkout-buttons">
                  <button className="back-to-cart-btn" onClick={handleBackToCart} disabled={loading}>
                    ← Back to cart
                  </button>
                  <button
                    className="continue-to-payment-btn"
                    onClick={handleContinueToPayment}
                    disabled={loading || cartItems.length === 0}
                  >
                    Continue to payment
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Payment Step */}
          {currentStep === "payment" && (
            <>
              <h2 className="checkout-title">Payment</h2>
              {errors.submit && <div className="error-message">{errors.submit}</div>}
              {showLoginOption && !isAuthenticated ? (
                <div className="login-option-section">
                  <div className="login-prompt">
                    <h3>Optional: Login for better experience</h3>
                    <p>You can continue as guest or login to track your order.</p>
                    <div className="login-options">
                      <button className="skip-login-btn" onClick={handleSkipLogin}>
                        Continue as guest
                      </button>
                      <button className="login-btn" onClick={handleLoginRedirect}>
                        Login to your account
                      </button>
                    </div>
                    <p className="login-benefits">
                      Benefits of logging in:
                      <ul>
                        <li>Track your order status</li>
                        <li>View order history</li>
                        <li>Save addresses for faster checkout</li>
                      </ul>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="checkout-form">
                  <div className="secure-payment-notice">
                    <p>All transactions are secure and encrypted.</p>
                  </div>
                  
                  {/* Payment method selection - ONLY OPTIONS, NO DETAIL FIELDS */}
                  <div className="payment-methods">
                    <div className={`payment-method ${selectedPaymentMethod === "card" ? "active" : ""}`}>
                      <input
                        type="radio"
                        id="credit-card"
                        name="payment-method"
                        checked={selectedPaymentMethod === "card"}
                        onChange={() => handlePaymentMethodChange("card")}
                      />
                      <label htmlFor="credit-card">Credit/Debit Card</label>
                    </div>
                    
                    <div className={`payment-method ${selectedPaymentMethod === "upi" ? "active" : ""}`}>
                      <input
                        type="radio"
                        id="upi"
                        name="payment-method"
                        checked={selectedPaymentMethod === "upi"}
                        onChange={() => handlePaymentMethodChange("upi")}
                      />
                      <label htmlFor="upi">UPI (Google Pay, PhonePe, Paytm)</label>
                    </div>
                    
                    <div className={`payment-method ${selectedPaymentMethod === "netbanking" ? "active" : ""}`}>
                      <input
                        type="radio"
                        id="netbanking"
                        name="payment-method"
                        checked={selectedPaymentMethod === "netbanking"}
                        onChange={() => handlePaymentMethodChange("netbanking")}
                      />
                      <label htmlFor="netbanking">Net Banking</label>
                    </div>
                    
                    <div className={`payment-method ${selectedPaymentMethod === "cod" ? "active" : ""}`}>
                      <input
                        type="radio"
                        id="cod"
                        name="payment-method"
                        checked={selectedPaymentMethod === "cod"}
                        onChange={() => handlePaymentMethodChange("cod")}
                      />
                      <label htmlFor="cod">Cash on Delivery (COD)</label>
                    </div>
                  </div>

                  {/* Payment method descriptions - NO INPUT FIELDS */}
                  {selectedPaymentMethod === "card" && (
                    <div className="payment-method-description">
                      <p>💳 Pay securely with your Credit or Debit Card</p>
                      <p className="payment-note">You will be redirected to our secure payment gateway</p>
                    </div>
                  )}

                  {selectedPaymentMethod === "upi" && (
                    <div className="payment-method-description">
                      <p>📱 Pay using UPI apps like Google Pay, PhonePe, or Paytm</p>
                      <p className="payment-note">You will be redirected to complete the payment</p>
                    </div>
                  )}
                  
                  {selectedPaymentMethod === "netbanking" && (
                    <div className="payment-method-description">
                      <p>🏦 Pay using Net Banking from major Indian banks</p>
                      <p className="payment-note">You will be redirected to your bank's secure portal</p>
                    </div>
                  )}
                  
                  {selectedPaymentMethod === "cod" && (
                    <div className="payment-method-description">
                      <p>💵 Pay with cash when your order is delivered</p>
                      <p className="payment-note">No additional charges for COD</p>
                    </div>
                  )}

                  <div className="checkout-buttons">
                    <button className="back-to-cart-btn" onClick={handleBackToDelivery} disabled={loading}>
                      ← Back to delivery
                    </button>
                    <button className="pay-now-btn" onClick={handlePayNow} disabled={loading || cartItems.length === 0}>
                      {loading ? "Processing..." : "Pay now"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Review (Confirmation) Step */}
          {currentStep === "review" && (
            <>
              <h2 className="checkout-title">Order Review</h2>
              <div className="review-section">
                <div className="order-confirmation">
                  <div className="confirmation-header">
                    <div className="confirmation-icon">✓</div>
                    <h3>Order Confirmed!</h3>
                    <p className="order-id">
                      Order ID: {JSON.parse(localStorage.getItem("lastOrder") || "{}").orderId || "N/A"}
                    </p>
                    {paymentDetails.transactionId && (
                      <p className="transaction-id">
                        Transaction ID: {paymentDetails.transactionId}
                      </p>
                    )}
                  </div>
                  <div className="confirmation-details">
                    <div className="detail-row">
                      <span className="detail-label">Order Date:</span>
                      <span className="detail-value">
                        {new Date().toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Total Amount:</span>
                      <span className="detail-value total-amount">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Payment Method:</span>
                      <span className="detail-value">
                        {selectedPaymentMethod === "cod"
                          ? "Cash on Delivery"
                          : selectedPaymentMethod === "upi"
                          ? "UPI"
                          : selectedPaymentMethod === "netbanking"
                          ? "Net Banking"
                          : "Credit/Debit Card"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Status:</span>
                      <span className="detail-value status-confirmed">Confirmed</span>
                    </div>
                  </div>
                  <div className="shipping-info-review">
                    <h4>Shipping Address</h4>
                    <p>
                      {formData.firstName} {formData.lastName}
                      <br />
                      {formData.address}
                      <br />
                      {formData.apartment && (
                        <>
                          {formData.apartment}
                          <br />
                        </>
                      )}
                      {formData.city}, {formData.state} {formData.pinCode}
                      <br />
                      {formData.country}
                      <br />
                      📞 {formData.phone}
                    </p>
                  </div>
                  <div className="order-items-review">
                    <h4>Order Items</h4>
                    <div className="review-items-list">
                      {cartItems.map((item, index) => (
                        <div key={index} className="review-item">
                          <div className="review-item-image">
                            <img
                              src={item.image || item.img || "/images/product-placeholder.jpg"}
                              alt={item.name}
                              onError={(e) => {
                                e.target.src = "/images/product-placeholder.jpg";
                              }}
                            />
                          </div>
                          <div className="review-item-details">
                            <h5>{item.name || item.title}</h5>
                            <p className="review-item-qty">Quantity: {item.qty || 1}</p>
                            <p className="review-item-price">{formatPrice(item.price || item.priceValue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="review-actions">
                    <button className="continue-shopping-btn" onClick={handleContinueShopping}>
                      Continue Shopping
                    </button>
                    <button className="view-order-btn" onClick={handleViewOrderDetails}>
                      View Order Details
                    </button>
                  </div>
                  <div className="order-notes">
                    <p>📧 A confirmation email has been sent to {formData.email}</p>
                    <p>⏰ You will receive shipping updates within 24 hours</p>
                    <p>🔄 Need to make changes? Contact our customer support</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Order Summary (Right Column) */}
        <div className="checkout-right">
          <div className="order-summary">
            <h3 className="summary-title">Order Summary</h3>
            <div className="summary-items">
              {cartItems.length > 0 ? (
                cartItems.map((item) => (
                  <div key={item.uniqueId || item.id} className="summary-item">
                    <div className="product-img">
                      <img
                        src={item.image || item.img || "/images/product-placeholder.jpg"}
                        alt={item.name}
                        onError={(e) => {
                          e.target.src = "/images/product-placeholder.jpg";
                        }}
                      />
                      <span className="qty">{item.qty || 1}</span>
                    </div>
                    <div className="summary-info">
                      <p className="product-name">{item.name || item.title}</p>
                      <span className="product-price">{formatPrice(item.price || item.priceValue)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-cart-message">Your cart is empty</p>
              )}
            </div>
            <div className="price-section">
              <div className="price-row">
                <span>Subtotal</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="price-row">
                <span>Shipping</span>
                <span className="free-shipping">Enter shipping address</span>
              </div>
              <div className="price-divider"></div>
              <div className="price-row total">
                <span>Total</span>
                <span className="total-price">{formatPrice(subtotal)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Popup */}
      {showPaymentPopup && (
        <div className="payment-popup-overlay" onClick={handleClosePopup}>
          <div 
            className={`payment-popup ${paymentStatus === 'success' ? 'success-popup' : 'failure-popup'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-icon">
              {paymentStatus === 'success' ? '✓' : '✗'}
            </div>
            <h3>
              {paymentStatus === 'success' ? 'Payment Successful!' : 'Payment Failed'}
            </h3>
            
            {paymentStatus === 'success' ? (
              <>
                <p>Your payment has been processed successfully.</p>
                {paymentDetails.orderId && (
                  <p className="popup-order-id">Order ID: {paymentDetails.orderId}</p>
                )}
                {paymentDetails.transactionId && (
                  <p className="popup-transaction-id">Transaction ID: {paymentDetails.transactionId}</p>
                )}
                {paymentDetails.amount && (
                  <p className="popup-amount">Amount: {formatPrice(paymentDetails.amount)}</p>
                )}
                <p>Thank you for your purchase!</p>
              </>
            ) : (
              <>
                <p>We couldn't process your payment.</p>
                {paymentDetails.errorMessage && (
                  <p className="popup-error">{paymentDetails.errorMessage}</p>
                )}
                <p>Please try again or use a different payment method.</p>
              </>
            )}
            
            <button className="popup-close-btn" onClick={handleClosePopup}>
              {paymentStatus === 'success' ? 'Continue' : 'Close'}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </>
  );

  // Helper for skip login
  async function handleSkipLogin() {
    setShowLoginOption(false);
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    await handlePayNow();
  }
};

export default CheckoutPage;